import { Component, Input, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { iif, Observable, Subject } from 'rxjs';
import { filter, switchMap, takeUntil, tap } from 'rxjs/operators';

import {
    get_access_token_for_no_iframe,
    get_company_lang,
    get_company_roles,
    init_company_session,
} from '@rpt-ngrx/actions/session.action';
import {
    selectAccessTokenSet,
    selectCompanies,
    selectCompanyId,
    selectIsIframeMode,
    selectSessionReady,
    selectUser,
    showCompanySelector,
} from '@rpt-ngrx/selectors/session.selector';
import { selectSelectedMenuItem, selectSidebarLoader } from '@rpt-ngrx/selectors/ui.selector';
import { SharedService } from '@services/shared-service';
import { IMenuItem, IUser } from '@shared/types';
import { ICompany } from '@shared/types/session';
import { XUXDrawerSidebarService } from '@xenial-ux/services';

@Component({
    selector: 'default-layout',
    templateUrl: 'default.component.html',
    providers: [SharedService],
})
export class DefaultLayoutComponent implements OnInit {
    boxed: boolean;
    compress: boolean;
    menuStyle: string;
    rtl: boolean;

    private unsubscribe$: Subject<void> = new Subject<void>();
    private currentCompanyId: string;

    public isIframeMode$: Observable<boolean>;
    public user$: Observable<IUser>;
    public selectedMenuItem$: Observable<IMenuItem | string>;
    public contentLoader$: Observable<boolean>;
    public predefinedReportFilters = false;

    public companies$: Observable<Array<ICompany>>;
    public accessTokenSet$: Observable<boolean>;
    public currentCompanyId$: Observable<string>;
    public showCompanySelector$: Observable<boolean>;
    public sessionReady$: Observable<boolean>;

    @Input() openedSidebar: boolean;

    constructor(
        private drawerSidebarService: XUXDrawerSidebarService,
        private store: Store,
        private translate: TranslateService,
        private activatedRoute: ActivatedRoute,
    ) {
        this.openedSidebar = false;
        this.boxed = false;
        this.compress = false;
        this.menuStyle = 'style-3';
        this.rtl = false;
    }

    ngOnInit() {
        this.selectedMenuItem$ = this.store.select(selectSelectedMenuItem);
        this.contentLoader$ = this.store.select(selectSidebarLoader);
        this.isIframeMode$ = this.store.select(selectIsIframeMode);
        this.user$ = this.store.select(selectUser);
        this.companies$ = this.store.select(selectCompanies);
        this.currentCompanyId$ = this.store.select(selectCompanyId);
        this.showCompanySelector$ = this.store.select(showCompanySelector);
        this.accessTokenSet$ = this.store.select(selectAccessTokenSet);
        this.sessionReady$ = this.store.select(selectSessionReady);

        this.activatedRoute.queryParams
            .pipe(
                takeUntil(this.unsubscribe$),
                tap((params) => {
                    if (params.target) {
                        this.predefinedReportFilters = true;
                    }
                }),
                switchMap(() => this.user$),
            )
            .subscribe((user) => {
                if (user && !this.predefinedReportFilters) {
                    this.drawerSidebarService.openOnDesktop();
                }
            });

        this.sessionReady$
            .pipe(
                filter((sessionReady) => !!sessionReady),
                switchMap(() =>
                    this.isIframeMode$.pipe(
                        switchMap((isIframe) =>
                            iif(
                                () => isIframe,
                                this.store.select(selectCompanyId).pipe(
                                    filter((companyId) => !!companyId),
                                    switchMap((companyId) =>
                                        this.accessTokenSet$.pipe(
                                            filter((accessTokenSet) => !!accessTokenSet),
                                            switchMap(() =>
                                                this.user$.pipe(
                                                    tap((user) => {
                                                        this.store.dispatch(get_company_lang({ companyId: companyId }));
                                                        if (user) {
                                                            this.store.dispatch(
                                                                get_company_roles({ companyId: companyId, user: user }),
                                                            );
                                                        }
                                                    }),
                                                ),
                                            ),
                                        ),
                                    ),
                                ),
                                this.user$.pipe(
                                    tap((user) => {
                                        this.store.dispatch(
                                            init_company_session({ companyId: this.currentCompanyId, user }),
                                        );
                                    }),
                                ),
                            ),
                        ),
                    ),
                ),
            )
            .subscribe(() => {});
    }

    public selectCompany(companyId) {
        this.currentCompanyId = companyId;
        this.store.dispatch(get_access_token_for_no_iframe({ companyId: companyId }));
    }
}
